import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { IconKey, IconSignOut } from '../primitives/Icons';

interface AccountMenuProps {
  /** Whether to render the mobile bottom sheet (true) or desktop popover (false). */
  isMobile: boolean;
  /**
   * Whether the user has a password credential. When false (social-only, e.g.
   * Google), the change-password item is disabled with an explanatory helper
   * line, since there's no password to change.
   */
  hasPassword: boolean;
  onChangePassword: () => void;
  onSignOut: () => void;
  onClose: () => void;
}

/**
 * Account actions opened from the sidebar user row. Mirrors the existing
 * RowMenu (desktop) / ActionSheet (mobile) patterns. The desktop popover is
 * absolutely positioned above its trigger (the user row sits at the bottom of
 * the sidebar), so the parent renders it inside a position:relative container.
 */
export function AccountMenu({
                              isMobile,
                              hasPassword,
                              onChangePassword,
                              onSignOut,
                              onClose,
                            }: AccountMenuProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    // mousedown so the menu closes before any underlying click fires.
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const changePw = () => {
    onChangePassword();
    onClose();
  };

  if (isMobile) {
    return (
      <div className="sheet-overlay" onClick={onClose} role="presentation">
        <div
          ref={ref}
          className="sheet"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
        >
          <div className="sheet-title">{t('accountMenuTitle')}</div>

          {hasPassword ? (
            <button type="button" className="sheet-item" onClick={changePw}>
              <IconKey />
              <span>{t('changePassword')}</span>
            </button>
          ) : (
            <>
              <div className="sheet-item shi-disabled" aria-disabled="true">
                <IconKey />
                <span>{t('changePassword')}</span>
              </div>
              <div className="sheet-helper">{t('passwordManagedByGoogle')}</div>
            </>
          )}

          <button type="button" className="sheet-item" onClick={onSignOut}>
            <IconSignOut />
            <span>{t('signOut')}</span>
          </button>

          <div className="sheet-divider" />
          <button type="button" className="sheet-cancel" onClick={onClose}>
            {t('cancel')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div ref={ref} className="account-popover" role="menu">
      {hasPassword ? (
        <button
          type="button"
          className="row-popover-item"
          onClick={changePw}
          role="menuitem"
        >
          <IconKey />
          <span>{t('changePassword')}</span>
        </button>
      ) : (
        <>
          <div className="row-popover-item rpi-disabled" role="menuitem" aria-disabled="true">
            <IconKey />
            <span>{t('changePassword')}</span>
          </div>
          <div className="helper-line">{t('passwordManagedByGoogle')}</div>
        </>
      )}

      <button
        type="button"
        className="row-popover-item"
        onClick={onSignOut}
        role="menuitem"
      >
        <IconSignOut />
        <span>{t('signOut')}</span>
      </button>
    </div>
  );
}
